import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFeature, FEATURES } from 'modules/entitlements';
import {
  countTestFilters,
  useTestFilterAuthors,
  useTestFilterCollections,
  type NamedOption,
  type TestFilterValue,
} from 'components/tests/testFilterOptions';
import { DateRangeChips, FilterSheet, OptionRows, SheetSection } from '../filterSheet';

interface TestFilterSheetProps {
  open: boolean;
  onClose: () => void;
  orgId?: string;
  currentUserId?: string;
  value: TestFilterValue;
  onChange: (next: TestFilterValue) => void;
  /** Results under the current filters — updates live behind the sheet. */
  total: number;
}

/** The phone form of the Tests filter line: filters apply as they are picked. */
export function TestFilterSheet({ open, onClose, orgId, currentUserId, value, onChange, total }: TestFilterSheetProps) {
  const { t } = useTranslation();
  const canUseCollections = useFeature(FEATURES.COLLECTIONS);
  const authors = useTestFilterAuthors(orgId, open || Boolean(value.createdById));
  const collections = useTestFilterCollections(orgId, canUseCollections && (open || Boolean(value.collectionId)));

  const authorOptions: NamedOption[] = [
    ...(currentUserId ? [{ id: currentUserId, name: t('created_by_me') }] : []),
    ...(authors.data ?? []).filter((author) => author.id !== currentUserId),
  ];

  return (
    <FilterSheet
      open={open}
      onClose={onClose}
      onClear={countTestFilters(value) > 0 ? () => onChange({}) : undefined}
      total={total}
    >
      <SheetSection label={t('filter_author')}>
        <OptionRows
          name={t('filter_author')}
          options={authorOptions}
          selected={value.createdById}
          onSelect={(createdById) => onChange({ ...value, createdById })}
          loading={authors.isLoading}
          emptyLabel={authors.isError ? t('failed_to_load') : t('no_authors')}
        />
      </SheetSection>

      {canUseCollections && (
        <SheetSection label={t('collection')}>
          <OptionRows
            name={t('collection')}
            options={collections.data ?? []}
            selected={value.collectionId}
            onSelect={(collectionId) => onChange({ ...value, collectionId })}
            loading={collections.isLoading}
            emptyLabel={collections.isError ? t('failed_to_load') : t('no_collections')}
          />
        </SheetSection>
      )}

      <SheetSection label={t('created_at')}>
        <DateRangeChips
          value={{ from: value.createdFrom, to: value.createdTo }}
          onChange={({ from, to }) => onChange({ ...value, createdFrom: from, createdTo: to })}
        />
      </SheetSection>
    </FilterSheet>
  );
}

export default TestFilterSheet;
